import React, { useEffect, useState } from 'react'
import { ws } from 'shared/store/ws'
import { Bell, Close } from 'app/assets/icons'
import { FormattedMessage } from 'react-intl'
import { H4, Text, TinyText } from 'app/common/typography'
import styled from 'styled-components'
import Modal from 'react-modal'
import { media, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { useDispatch, useSelector } from 'react-redux'
import { toggleNotificationsEnabled } from 'shared/store/user'
import { dateDifferenceInDays } from 'shared/utils'
import type { RootState } from 'shared/store/reducers'
import { Loader } from 'app/common/loader'

const { text, yellow, error: errorColor } = colors
const NON_SOLICITATION_PERIOD = 30
const MIN_PAGES_BEFORE_SOLICITATION = 2

const NotificationsPermission = () => {
    const dispatch = useDispatch()
    const [visible, setVisible] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState()
    const { consentDate, numberOfPagesVisited } = useSelector(
        (state: RootState) => ({
            consentDate: state.userState.notificationsConsentDate,
            numberOfPagesVisited: state.userState.numberOfPagesVisited
        })
    )

    const checkPermission = async () => {
        try {
            const sdk = await window.UrbanAirship
            const registeredForNotifications =
                !!sdk.channel && sdk.permission === 'granted'
            const today = new Date()
            setVisible(
                sdk.canRegister &&
                    !registeredForNotifications &&
                    (!consentDate ||
                        dateDifferenceInDays(today, consentDate) >=
                            NON_SOLICITATION_PERIOD)
            )
        } catch (error) {
            console.log('Airship error', error)
        }
    }

    useEffect(() => {
        checkPermission()
    }, [])

    const handleClick = async () => {
        let errorValue
        try {
            setLoading(true)
            dispatch(toggleNotificationsEnabled(false))
            const sdk = await window.UrbanAirship
            if (sdk && sdk.canRegister) {
                const channel = await sdk.register()
                if (channel) {
                    const wsHeaders = ws.defaults.headers
                    wsHeaders['x-web-airship'] = channel.id
                }
            }
        } catch (error) {
            errorValue = error
        } finally {
            setLoading(false)
            errorValue ? setError(errorValue) : setVisible(false)
        }
    }

    const handleClose = () => {
        dispatch(toggleNotificationsEnabled(true))
        setVisible(false)
    }

    return (
        <Modal
            isOpen={
                visible && numberOfPagesVisited >= MIN_PAGES_BEFORE_SOLICITATION
            }
            ariaHideApp={false}
            shouldCloseOnOverlayClick
            onRequestClose={handleClose}
            style={style.classicModalStyle}>
            <Container>
                <CloseContainer onClick={handleClose}>
                    <Close color={text} size={24} />
                </CloseContainer>
                <Row>
                    <Bell size={30} />
                    <FormattedMessage id="notifications">
                        {(text: string) => <StyledH4 center>{text}</StyledH4>}
                    </FormattedMessage>
                </Row>
                <Content>
                    <Text>
                        <FormattedMessage id="modal.notifications.permission" />
                    </Text>
                </Content>
                <ButtonContainer>
                    <LoaderContainer>
                        <Loader loading={loading} />
                        {!!error && (
                            <ErrorText color={errorColor}>
                                <FormattedMessage id="modal.notifications.permission.error" />
                            </ErrorText>
                        )}
                    </LoaderContainer>
                    <Button
                        disabled={loading}
                        background={yellow}
                        border="none"
                        onClick={handleClick}>
                        <FormattedMessage id="yes.i.do" />
                    </Button>
                </ButtonContainer>
            </Container>
        </Modal>
    )
}

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`

const StyledH4 = styled(H4)`
    margin-left: 10px;
`

const Content = styled.div`
    margin: 20px 0 10px;
    ${media.medium`
        padding-left: 20px;
    `};
`

const ErrorText = styled(TinyText)`
    align-self: flex-start;
    ${media.medium`
        padding-left: 20px;
    `};
`

const Container = styled.div`
    min-width: 200px;
    padding: 20px;
    background: white;
    align-items: center;
    border-radius: 10px;
`

const CloseContainer = styled.div`
    position: absolute;
    top: 20px;
    cursor: pointer;
`

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const Button = styled.button`
    ${style.buttonSmall}
    ${media.medium`
        ${style.buttonMedium}
    `};
    padding-left: 30px;
    padding-right: 30px;
`

const LoaderContainer = styled.div`
    width: 100%;
    min-height: 30px;
`

export { NotificationsPermission }
